import React, { useRef, useEffect, useState } from 'react';
import { snapshot_ChartHeaderButtons as ChartHeaderButtons } from './snapshot_ChartHeaderButtons';
import useResizeObserver from '@/hooks/useResizeObserver';
import { useSnapshotRow } from '../contexts/SnapshotRowContext';
import { useShareUrl } from '@/hooks/useShareUrl';
import { downloadChart } from '@/utils/chartDownload';
import { snapshotMetricDefs } from '../data/SnapshotAboutTheDataDefs';

interface ChartCardProps {
  title: string;
  description: string;
  children: React.ReactNode;
  className?: string;
  rowIndex: number;
  cardIndex: number;
  chartName?: string;
  shareLink?: string;
}

export const snapshot_ChartCard: React.FC<ChartCardProps> = ({
  title,
  description,
  children,
  className = "",
  rowIndex,
  cardIndex,
  chartName,
  shareLink
}) => {
  const [isDownloading, setIsDownloading] = useState(false);
  const [isDownloadPopupOpen, setIsDownloadPopupOpen] = useState(false);
  
  const headerRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<HTMLDivElement>(null);
  
  const { rowHeights, headerHeights, updateHeight, updateHeaderHeight, removeCard } = useSnapshotRow();
  const { height: headerHeight } = useResizeObserver(headerRef);
  const { height: contentHeight } = useResizeObserver(contentRef);
  
  // Use provided shareLink or fall back to current URL
  const currentShareLink = useShareUrl();
  const finalShareLink = shareLink || currentShareLink;
  const finalChartName = chartName || title;
  const metricDef = snapshotMetricDefs[finalChartName as keyof typeof snapshotMetricDefs];
  
  // Download functionality - following the same pattern as peer jurisdiction cards
  const downloadSingleChart = async () => {
    if (!chartRef.current) {
      console.log('No chart ref found');
      return;
    }
    
    console.log('Starting download for snapshot chart:', title);
    
    // Add download styling classes similar to peer cards
    const labels = chartRef.current.querySelectorAll('.download-jurisdiction-label');
    labels.forEach(label => label.classList.add('download-shift-up'));

    // Hide header action buttons during download
    const headerButtonsContainer = chartRef.current.querySelector<HTMLElement>('.chart-action-buttons');
    headerButtonsContainer?.classList.add('download-chart-headers-hide');
    
    // Inject style if not present
    let styleTag = document.getElementById('download-shift-up-style');
    if (!styleTag) {
      styleTag = document.createElement('style');
      styleTag.id = 'download-shift-up-style';
      styleTag.innerHTML = `
        .download-shift-up {overflow: visible !important; position: relative !important; top: -8px !important;}
        .download-chart-headers-hide {display: none !important;}
      `;
      document.head.appendChild(styleTag);
    }
    
    setIsDownloading(true);
    try {
      await downloadChart(chartRef.current, `${finalChartName.toLowerCase().replace(/\s+/g, '-')}`);
    } catch (error) {
      console.error('Error in snapshot chart download:', error);
    } finally {
      // Clean up classes after download
      labels.forEach(label => label.classList.remove('download-shift-up'));
      headerButtonsContainer?.classList.remove('download-chart-headers-hide');
      setIsDownloading(false);
    }
  };

  const downloadAllCharts = async () => {
    console.log('[downloadAllCharts] Starting batch download for snapshot charts');
    setIsDownloading(true);
    try {
      const articles = Array.from(document.querySelectorAll<HTMLElement>('article[data-chart-title]'));
      for (const el of articles) {
        const titleAttr = el.dataset.chartTitle || 'chart';
        await downloadChart(el, titleAttr.toLowerCase().replace(/\s+/g, '-'));
        // Slight delay ensures the browser writes each file sequentially
        await new Promise(res => setTimeout(res, 300));
      }
    } catch (error) {
      console.error('[downloadAllCharts] Error:', error);
    } finally {
      setIsDownloading(false);
    }
  };

  const handleDownloadOption = async (option: 'single' | 'all') => {
    setIsDownloadPopupOpen(false);
    if (option === 'single') {
      await downloadSingleChart();
    } else {
      await downloadAllCharts();
    }
  };

  const handleDownloadButtonClick = () => {
    if (isDownloading) return; // guard against concurrent downloads
    setIsDownloadPopupOpen(true);
  };

  useEffect(() => {
    updateHeight(rowIndex, cardIndex, headerHeight);
  }, [updateHeight, rowIndex, cardIndex, headerHeight]);

  useEffect(() => {
    updateHeaderHeight(rowIndex, cardIndex, contentHeight);
  }, [updateHeaderHeight, rowIndex, cardIndex, contentHeight]);

  useEffect(() => {
    // Cleanup function
    return () => {
      removeCard(rowIndex, cardIndex);
    };
  }, [removeCard, rowIndex, cardIndex]);

  // Get the maximum heights for this row
  const rowMaxHeight = rowHeights.get(rowIndex) || 0;
  const headerMaxHeight = headerHeights.get(rowIndex) || 0;

  return (
    <article 
      ref={chartRef}
      className={`bg-white w-full min-w-[280px] rounded-[20px] relative flex flex-col h-full ${className}`}
      data-chart-title={finalChartName}
    >
      <ChartHeaderButtons 
        chartName={finalChartName} 
        shareLink={finalShareLink}
        isDownloading={isDownloading}
        isDownloadPopupOpen={isDownloadPopupOpen}
        onDownloadClick={handleDownloadButtonClick}
        onDownloadOption={handleDownloadOption}
        onDownloadPopupClose={() => setIsDownloadPopupOpen(false)}
        metricDefinition={metricDef}
        aboutDescription={description}
      />
      <header 
        ref={headerRef} 
        className="w-full px-6 relative max-md:max-w-full max-md:px-5 flex-shrink-0 flex flex-col after:content-[''] after:absolute after:bottom-0 after:inset-x-6 after:max-md:inset-x-5 after:h-[4px] after:bg-gradient-to-r after:from-[#CA0079] after:to-[#F1AB11] after:rounded-full"
        style={{ 
          minHeight: `${rowMaxHeight}px`,
          transition: 'min-height 0.2s ease-in-out',
          paddingTop: '24px',
          paddingBottom: '24px'
        }}
      >
        <div 
          ref={contentRef}
          className="w-full flex flex-col"
          style={{
            minHeight: `${headerMaxHeight}px`,
            transition: 'min-height 0.2s ease-in-out'
          }}
        >
          <div className="w-full max-md:max-w-full">
            <div className="flex w-full items-center gap-6 text-[28px] text-[#212121] font-semibold leading-[1.3] max-md:max-w-full">
              <h2 className="text-[#212121] self-stretch flex-1 shrink basis-[0%] my-auto max-md:max-w-full leading-tight align-middle chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
                {title}
              </h2>
            </div>
            <p className="text-[#212121] text-xl font-normal leading-tight align-middle mt-3 max-md:max-w-full chart-text-shift" style={{lineHeight: '1.2', verticalAlign: 'middle'}}>
              {description}. Brought to you by Govstream.ai.
            </p>
          </div>
        </div>
      </header>
      <div className="flex-1 px-6 flex flex-col justify-end max-md:px-5 max-donut:px-4">
        {children}
      </div>
    </article>
  );
};